// =============================================================================
// BP-07-03: Resource Search Page
// Full-featured search results page with filters, infinite scroll, and
// search history
// =============================================================================

import React, { useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Search, X, Clock, ArrowLeft, Filter, Loader2 } from 'lucide-react';
import { useInfiniteResourceSearch, useDiagnosisSearch } from '../hooks/useResourceSearch';
import { useResourceFilterStore, useSearchHistoryStore } from '../stores/resourceStore';
import {
  ResourceGrid,
  ResourceFilters,
  ResourceSearchBar,
  ResourceBreadcrumb,
} from '../components';

// =============================================================================
// Search Results Summary
// =============================================================================

interface SearchSummaryProps {
  query: string;
  totalResults: number;
  isLoading: boolean;
}

const SearchSummary: React.FC<SearchSummaryProps> = ({
  query,
  totalResults,
  isLoading,
}) => {
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-warm-gray">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span>Searching for "{query}"...</span>
      </div>
    );
  }

  return (
    <div className="text-navy">
      <span className="font-medium">{totalResults.toLocaleString()}</span>
      <span className="text-warm-gray"> results for </span>
      <span className="font-semibold">"{query}"</span>
    </div>
  );
};

// =============================================================================
// Search History Panel
// =============================================================================

interface SearchHistoryPanelProps {
  onSearch: (query: string) => void;
}

const SearchHistoryPanel: React.FC<SearchHistoryPanelProps> = ({ onSearch }) => {
  const { searchHistory, removeFromHistory, clearHistory } = useSearchHistoryStore();

  if (!searchHistory?.length) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg border border-warm-gray/10 p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-navy flex items-center gap-2">
          <Clock className="w-4 h-4 text-warm-gray" />
          Recent Searches
        </h3>
        <button
          onClick={clearHistory}
          className="text-xs text-warm-gray hover:text-terracotta transition-colors"
        >
          Clear all
        </button>
      </div>
      <div className="flex flex-wrap gap-2">
        {searchHistory.map((search, index) => (
          <button
            key={index}
            onClick={() => onSearch(search)}
            className="group flex items-center gap-1.5 px-3 py-1.5 bg-cream rounded-full
                       text-sm text-navy hover:bg-sage/10 transition-colors"
          >
            <span>{search}</span>
            <X
              className="w-3.5 h-3.5 text-warm-gray opacity-0 group-hover:opacity-100
                         hover:text-terracotta transition-all"
              onClick={(e) => {
                e.stopPropagation();
                removeFromHistory(search);
              }}
            />
          </button>
        ))}
      </div>
    </div>
  );
};

// =============================================================================
// Diagnosis Results Section
// =============================================================================

interface DiagnosisResultsProps {
  query: string;
}

const DiagnosisResults: React.FC<DiagnosisResultsProps> = ({ query }) => {
  const { data: diagnoses, isLoading } = useDiagnosisSearch(query);

  if (isLoading || !diagnoses || diagnoses.length === 0) {
    return null;
  }

  return (
    <div className="mb-8">
      <h2 className="text-lg font-display font-semibold text-navy mb-4">
        Related Diagnoses
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {diagnoses.slice(0, 3).map((diagnosis) => (
          <Link
            key={diagnosis.id}
            to={`/diagnoses/${diagnosis.slug}`}
            className="flex items-start gap-4 p-4 bg-white rounded-lg border border-warm-gray/10
                       hover:border-sage/30 hover:shadow-md transition-all group"
          >
            {diagnosis.icon_url ? (
              <img
                src={diagnosis.icon_url}
                alt=""
                className="w-12 h-12 rounded-lg object-cover"
              />
            ) : (
              <div className="w-12 h-12 rounded-lg bg-sage/10 flex items-center justify-center">
                <span className="text-sage font-display font-bold text-lg">
                  {diagnosis.short_name?.[0] || diagnosis.name[0]}
                </span>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-navy group-hover:text-sage transition-colors truncate">
                {diagnosis.name}
              </h3>
              {diagnosis.short_name && diagnosis.short_name !== diagnosis.name && (
                <p className="text-sm text-warm-gray">{diagnosis.short_name}</p>
              )}
              <p className="text-xs text-sage mt-1">
                {diagnosis.resource_count} resources
              </p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

// =============================================================================
// No Results State
// =============================================================================

interface NoResultsProps {
  query: string;
  onClearFilters: () => void;
  hasActiveFilters: boolean;
}

const NoResults: React.FC<NoResultsProps> = ({
  query,
  onClearFilters,
  hasActiveFilters,
}) => {
  return (
    <div className="text-center py-16">
      <div className="w-16 h-16 bg-cream rounded-full flex items-center justify-center mx-auto mb-4">
        <Search className="w-8 h-8 text-warm-gray" />
      </div>
      <h2 className="text-xl font-display font-semibold text-navy mb-2">
        No results found
      </h2>
      <p className="text-warm-gray mb-6 max-w-md mx-auto">
        We couldn't find any resources matching "{query}".
        {hasActiveFilters && ' Try adjusting your filters or search terms.'}
      </p>
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        {hasActiveFilters && (
          <button
            onClick={onClearFilters}
            className="px-4 py-2 bg-cream text-navy rounded-lg hover:bg-cream-dark transition-colors"
          >
            Clear Filters
          </button>
        )}
        <Link
          to="/resources"
          className="px-4 py-2 bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors"
        >
          Browse All Resources
        </Link>
      </div>
    </div>
  );
};

// =============================================================================
// Empty Search State
// =============================================================================

interface EmptySearchProps {
  onSearch: (query: string) => void;
}

const EmptySearch: React.FC<EmptySearchProps> = ({ onSearch }) => {
  const popularSearches = [
    'therapy techniques',
    'IEP goals',
    'assistive technology',
    'early intervention',
    'insurance coverage',
    'home modifications',
  ];

  return (
    <div className="py-12">
      <div className="text-center mb-12">
        <div className="w-20 h-20 bg-sage/10 rounded-full flex items-center justify-center mx-auto mb-6">
          <Search className="w-10 h-10 text-sage" />
        </div>
        <h1 className="text-2xl font-display font-semibold text-navy mb-2">
          Search Resources
        </h1>
        <p className="text-warm-gray max-w-md mx-auto">
          Find articles, guides, and information to support your family's journey.
        </p>
      </div>

      <div className="max-w-2xl mx-auto space-y-8">
        <SearchHistoryPanel onSearch={onSearch} />

        <div>
          <h3 className="text-sm font-semibold text-navy mb-3">Popular Searches</h3>
          <div className="flex flex-wrap gap-2">
            {popularSearches.map((search) => (
              <button
                key={search}
                onClick={() => onSearch(search)}
                className="px-4 py-2 bg-white rounded-full border border-warm-gray/20
                           text-sm text-navy hover:border-sage hover:text-sage transition-colors"
              >
                {search}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// Main Page Component
// =============================================================================

const ResourceSearchPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') || '';

  const { clearFilters, setSearchQuery } = useResourceFilterStore();
  const { addToHistory } = useSearchHistoryStore();
  const hasActiveFilters = useResourceFilterStore(
    (state) => 
      !!state.categorySlug || 
      !!state.diagnosisSlug || 
      !!state.resourceType || 
      !!state.lifeStage ||
      state.tags.length > 0
  );

  // Mobile filter state
  const [showMobileFilters, setShowMobileFilters] = React.useState(false);

  // Search hook with infinite scroll
  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteResourceSearch({ q: query });

  // Flatten paginated results
  const resources = data?.pages.flatMap((page) => page.items) || [];
  const totalResults = data?.pages[0]?.total || 0;

  // Handle search
  const handleSearch = (newQuery: string) => {
    if (newQuery.trim()) {
      setSearchParams({ q: newQuery.trim() });
      addToHistory(newQuery.trim());
    }
  };

  // Sync search query to filter store
  useEffect(() => {
    setSearchQuery(query);
  }, [query, setSearchQuery]);

  // Handle clear filters
  const handleClearFilters = () => {
    clearFilters();
  };

  // Breadcrumb items
  const breadcrumbItems = [
    { label: 'Resources', href: '/resources' },
    { label: query ? `Search: ${query}` : 'Search' },
  ];

  return (
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <div className="bg-white border-b border-warm-gray/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <ResourceBreadcrumb items={breadcrumbItems} className="mb-4" />

          {/* Search Bar */}
          <div className="max-w-2xl">
            <ResourceSearchBar
              defaultValue={query}
              onSearch={handleSearch}
              placeholder="Search articles, guides, and resources..."
              autoFocus={!query}
              size="lg"
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {!query ? (
          <EmptySearch onSearch={handleSearch} />
        ) : (
          <div className="flex gap-8">
            {/* Sidebar Filters (Desktop) */}
            <aside className="hidden lg:block w-64 flex-shrink-0">
              <div className="sticky top-24">
                <ResourceFilters variant="sidebar" />
              </div>
            </aside>

            {/* Results */}
            <main className="flex-1 min-w-0">
              {/* Results Header */}
              <div className="flex items-center justify-between mb-6">
                <SearchSummary
                  query={query}
                  totalResults={totalResults}
                  isLoading={isLoading}
                />

                {/* Mobile Filter Button */}
                <button
                  onClick={() => setShowMobileFilters(true)}
                  className="lg:hidden flex items-center gap-2 px-4 py-2 bg-white rounded-lg
                             border border-warm-gray/20 text-navy hover:border-sage transition-colors"
                >
                  <Filter className="w-4 h-4" />
                  <span>Filters</span>
                  {hasActiveFilters && (
                    <span className="w-2 h-2 bg-sage rounded-full" />
                  )}
                </button>
              </div>

              {/* Diagnosis Results */}
              <DiagnosisResults query={query} />

              {/* Resource Results */}
              {resources.length > 0 ? (
                <ResourceGrid
                  resources={resources}
                  isLoading={isLoading}
                  showLoadMore={hasNextPage}
                  onLoadMore={() => fetchNextPage()}
                  isLoadingMore={isFetchingNextPage}
                />
              ) : !isLoading ? (
                <NoResults
                  query={query}
                  onClearFilters={handleClearFilters}
                  hasActiveFilters={hasActiveFilters}
                />
              ) : null}
            </main>
          </div>
        )}
      </div>

      {/* Mobile Filters Modal */}
      {showMobileFilters && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-navy/50"
            onClick={() => setShowMobileFilters(false)}
          />
          <div className="absolute inset-y-0 right-0 w-full max-w-sm bg-cream overflow-y-auto">
            <div className="sticky top-0 bg-cream border-b border-warm-gray/10 p-4 flex items-center justify-between">
              <h2 className="font-display font-semibold text-navy">Filters</h2>
              <button
                onClick={() => setShowMobileFilters(false)}
                className="p-2 hover:bg-white rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              <ResourceFilters variant="sidebar" />
            </div>
            <div className="sticky bottom-0 bg-cream border-t border-warm-gray/10 p-4 flex gap-3">
              <button
                onClick={() => {
                  handleClearFilters();
                  setShowMobileFilters(false);
                }}
                className="flex-1 px-4 py-2 border border-warm-gray/20 rounded-lg
                           text-navy hover:bg-white transition-colors"
              >
                Clear All
              </button>
              <button
                onClick={() => setShowMobileFilters(false)}
                className="flex-1 px-4 py-2 bg-sage text-white rounded-lg
                           hover:bg-sage-dark transition-colors"
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResourceSearchPage;



